import {
  PermissionCode,
  RoleCode,
  type BuiltInRoleCode,
  type PermissionCode as PermissionCodeValue
} from "../domain/identity/access-types.js";

const readOperational = [
  PermissionCode.EquipmentRead,
  PermissionCode.InspectionRead,
  PermissionCode.ReportRead,
  PermissionCode.DocumentRead,
  PermissionCode.CatalogRead,
  PermissionCode.NdtRegistryRead
] as const;

/**
 * PLH (Profissional Legalmente Habilitado) approval/signature capabilities.
 * These are technical capabilities tied to a qualified, registered
 * Professional — never granted automatically by role alone, not even to
 * ADMIN (Bloco 5, 2026-09-14). Exported so other layers (e.g. a future
 * Professional-qualification gate, or the permissions-editor UI) can
 * identify and treat these permissions specially without duplicating this
 * list.
 */
export const PLH_SPECIFIC_PERMISSIONS = [
  PermissionCode.PlhReview,
  PermissionCode.PlhApprove,
  PermissionCode.PlhReturn,
  PermissionCode.SignatureSign,
  PermissionCode.EngineeringApproveMinuta,
  PermissionCode.EngineeringSignMinuta,
  PermissionCode.IntegrityReportApprove,
  PermissionCode.IntegrityReportSign,
  PermissionCode.MeReportApprove,
  PermissionCode.MeReportSign,
  PermissionCode.LpReportApprove,
  PermissionCode.LpReportSign,
  PermissionCode.PmReportApprove,
  PermissionCode.PmReportSign,
  PermissionCode.UscReportApprove,
  PermissionCode.BscanReportApprove,
  PermissionCode.UscReportSign,
  PermissionCode.BscanReportSign,
  PermissionCode.AvrApprove,
  PermissionCode.AvrSign,
  PermissionCode.AvrPlhReview,
  PermissionCode.AvrRequestCorrection,
  PermissionCode.AvrApprovePlhReview,
  PermissionCode.TechnicalReviewReview,
  PermissionCode.TechnicalReviewRequestCorrection,
  PermissionCode.TechnicalReviewApprove,
  PermissionCode.TechnicalReviewSign,
  // Painel de Engenharia: aprovar e mandar para correção são atos do PLH designado.
  PermissionCode.EngineeringReviewApprove,
  PermissionCode.EngineeringReviewRequestCorrection
] as const;

/**
 * Permissões de Engenharia que NÃO são concedidas em bloco a ninguém — nem ao
 * ADMIN pelo simples fato de ser ADMIN. São ações técnicas sobre a numeração
 * ZR de uma Recommendation, próprias de quem responde pela engenharia do
 * documento. Continuam concedíveis a um usuário específico por
 * customPermissions, que são aditivas sobre o papel.
 *
 * Isto NÃO tem relação com PLH: editar ou remover um ZR não é assinar nada.
 */
/**
 * Permissões exclusivas de desenvolvedor:
 * Ferramentas de baixo nível e extensões que não pertencem à administração operacional comum.
 */
export const DEVELOPER_ONLY_PERMISSIONS = [
  PermissionCode.StudioCreate,
  PermissionCode.StudioUpdate,
  PermissionCode.StudioTest,
  PermissionCode.StudioInactivate,
  PermissionCode.StudioArchive
] as const;

export const ENGINEERING_SPECIFIC_PERMISSIONS = [
  PermissionCode.EngineeringEditZrNumber,
  PermissionCode.EngineeringRemoveZrNumber,
  // Escolher quem assina o Relatório LP (Inspetor, PLH, Fiscal) é decisão de
  // Engenharia sobre o documento — o ADMIN não a recebe por ser ADMIN.
  PermissionCode.LpReportManageSignatories,
  PermissionCode.PmReportManageSignatories,
  PermissionCode.UscReportManageSignatories,
  PermissionCode.BscanReportManageSignatories
] as const;

/**
 * PERMISSÕES BASE — todo perfil válido e ativo as recebe (decisão do usuário,
 * 2026-09-27: "equipment.read deve ser permissão base de todos os perfis").
 * Só LEITURA. O ESCOPO continua valendo à parte: equipment.read é "ler os
 * equipamentos AOS QUAIS a pessoa tem acesso" — o Cliente ativo, a restrição
 * de acesso ao Cliente (§37.1) e a leitura por id (§40) seguem decidindo o
 * quê. Escrita (create/update/delete/import…) nunca entra aqui.
 *
 * project.read/unit_area.read entraram aqui pelo mesmo motivo (AUD-007,
 * fechamento 2026-09-27): ProjectService.listProjects/getProject/listUnitAreas/
 * getUnitArea/listPendingUnitAreas não tinham NENHUMA checagem de autorização
 * — qualquer chamada RPC sem sessão os alcançava. Marcá-los apenas com a
 * permissão específica de cada perfil (em vez de base) regrediria Viewer,
 * Documentation, Manager, Developer e (só para unit_area.read) Inspector, que
 * hoje leem projetos/unidades-área com sucesso sem checagem nenhuma. A correção
 * real do AUD-007 é negar quem não tem contexto/sessão — não restringir quem
 * já tinha acesso de leitura de fato. O escopo (Cliente ativo, §37.1, §40)
 * continua decidindo o quê, igual a equipment.read.
 */
export const BASE_AUTHENTICATED_PERMISSIONS: readonly PermissionCodeValue[] = [
  PermissionCode.EquipmentRead,
  PermissionCode.ProjectRead,
  PermissionCode.UnitAreaRead
];

const ROLE_SPECIFIC_PERMISSIONS: Readonly<
  Record<BuiltInRoleCode, readonly PermissionCodeValue[]>
> = {
  [RoleCode.Viewer]: [
    PermissionCode.ClientAccess,
    PermissionCode.ClientRead,
    PermissionCode.EquipmentRead,
    PermissionCode.InspectionRead,
    PermissionCode.ReportRead,
    PermissionCode.DocumentRead,
    PermissionCode.CatalogRead,
    PermissionCode.NdtRegistryRead,
    PermissionCode.SubWorkOrderRead,
    PermissionCode.SubOsSearch,
    PermissionCode.ProfessionalRead,
    PermissionCode.RdoRead,
    PermissionCode.RdoCreate,
    PermissionCode.RdoUpdate,
    // ME (Bloco 11) — read-only, consistent with Viewer's read-only scope elsewhere.
    PermissionCode.MeReportView,
    PermissionCode.LpReportView,
    PermissionCode.PmReportView,
    PermissionCode.UscReportView,
    PermissionCode.BscanReportView
  ],
  [RoleCode.Inspector]: [
    PermissionCode.ClientAccess,
    PermissionCode.ClientRead,
    /**
     * LEITURA DO CONTEXTO DE TRABALHO (2026-09-25).
     *
     * Faltavam `project.read` e `professional.read`, e sem elas o Inspetor
     * entrava no sistema e não conseguia trabalhar: a tela do relatório
     * respondia "Você não possui permissão de leitura para Profissionais" e
     * o Projeto não carregava. Selecionar o Inspetor de um ensaio, ver o
     * signatário ou escolher o Projeto do documento são partes da atividade
     * dele, não administração.
     *
     * LER ≠ ADMINISTRAR: nenhuma permissão de criar, editar ou excluir
     * Projeto/Profissional é concedida aqui, e `administration.manage`
     * continua fora.
     */
    PermissionCode.ProjectRead,
    PermissionCode.ProfessionalRead,
    PermissionCode.EquipmentRead,
    // Bloco 02: a área EQUIPAMENTOS & ATIVOS é a mesma para Inspetor e Técnico —
    // leitura de Instrumentos e Dispositivos de Segurança (PSV) inclusive.
    PermissionCode.InstrumentRead,
    PermissionCode.SafetyDeviceRead,
    PermissionCode.InspectionRead,
    PermissionCode.InspectionReadAssigned,
    PermissionCode.InspectionCreate,
    PermissionCode.InspectionUpdate,
    // CONTROLE DE CAMPO: o Inspetor aponta o avanço das Linhas dos Sistemas a
    // que foi vinculado — e enxerga só esses. Não gerencia vínculos nem vê o
    // escopo dos outros Inspetores.
    PermissionCode.FieldControlViewOwn,
    PermissionCode.FieldControlUpdateOwn,
    PermissionCode.ChecklistRead,
    PermissionCode.ChecklistUpdate,
    // HOLD: quem elabora coloca e tira de HOLD (motivo obrigatório); a Engenharia comenta.
    PermissionCode.ReportPutOnHold,
    PermissionCode.ReportRemoveFromHold,
    PermissionCode.RecommendationRead,
    PermissionCode.RecommendationCreate,
    PermissionCode.RecommendationUpdate,
    PermissionCode.ReportRead,
    PermissionCode.ReportReadAssigned,
    PermissionCode.ReportCreate,
    PermissionCode.ReportUpdate,
    PermissionCode.RiRead,
    PermissionCode.RiCreate,
    PermissionCode.RiUpdate,
    PermissionCode.RdoRead,
    PermissionCode.RdoCreate,
    PermissionCode.RdoUpdate,
    PermissionCode.DocumentRead,
    PermissionCode.CatalogRead,
    PermissionCode.NdtRegistryRead,
    PermissionCode.NdtRead,
    // Fills the OS/Sub-OS of their own report from the official registry
    // (Bloco 19) — a document lookup, not administrative reach.
    PermissionCode.SubOsSearch,
    // ME (Bloco 11): Inspector fills and requests validation of ME reports,
    // mirroring the Ri*/NdtRegistry* footprint already granted above.
    PermissionCode.MeReportView,
    PermissionCode.MeReportCreate,
    PermissionCode.MeReportEdit,
    PermissionCode.MeReportValidate,
    PermissionCode.MeReportGeneratePdf,
    // LP: mesmo alcance do ME para o papel — Approve/Sign continuam só do PLH.
    PermissionCode.LpReportView,
    PermissionCode.LpReportCreate,
    PermissionCode.LpReportEdit,
    PermissionCode.LpReportValidate,
    PermissionCode.LpReportGeneratePdf,
    // PM e US-Convencional: mesmo alcance do LP para o papel — Approve/Sign continuam só do PLH.
    PermissionCode.PmReportView,
    PermissionCode.PmReportCreate,
    PermissionCode.PmReportEdit,
    PermissionCode.PmReportValidate,
    PermissionCode.PmReportGeneratePdf,
    PermissionCode.UscReportView,
    PermissionCode.BscanReportView,
    PermissionCode.UscReportCreate,
    PermissionCode.BscanReportCreate,
    PermissionCode.UscReportEdit,
    PermissionCode.BscanReportEdit,
    PermissionCode.UscReportValidate,
    PermissionCode.BscanReportValidate,
    PermissionCode.UscReportGeneratePdf,
    PermissionCode.BscanReportGeneratePdf,
    // Enviar o relatório ao PLH é ato de quem elabora; a fila é da Engenharia.
    PermissionCode.TechnicalReviewSubmitToPlh,
    // INSPECT IA REVIEW — revisar e corrigir o PRÓPRIO relatório (o que a tela
    // do módulo já deixa editar), consultar a revisão e usar o assistente.
    PermissionCode.AiReviewRead,
    PermissionCode.AiReviewExecute,
    PermissionCode.AiReviewApplyLanguage,
    PermissionCode.AiReviewResolveFinding,
    PermissionCode.AiReviewHistory,
    PermissionCode.AiAssistantUse
  ],
  [RoleCode.Technician]: [
    PermissionCode.ClientAccess,
    PermissionCode.ClientRead,
    // INSPECT IA REVIEW — revisar e corrigir o PRÓPRIO relatório (o que a tela
    // do módulo já deixa editar), consultar a revisão e usar o assistente.
    PermissionCode.AiReviewRead,
    PermissionCode.AiReviewExecute,
    PermissionCode.AiReviewApplyLanguage,
    PermissionCode.AiReviewResolveFinding,
    PermissionCode.AiReviewHistory,
    PermissionCode.AiAssistantUse,
    PermissionCode.ReportPutOnHold,
    PermissionCode.ReportRemoveFromHold,
    PermissionCode.ProjectRead,
    PermissionCode.UnitAreaRead,
    PermissionCode.EquipmentRead,
    PermissionCode.InstrumentRead,
    PermissionCode.SafetyDeviceRead,
    PermissionCode.InspectionRead,
    PermissionCode.InspectionReadAssigned,
    // CONTROLE DE CAMPO: o Técnico acompanha o avanço de todos os Inspetores
    // e é quem vincula Inspetor → Sistema de Tubulação.
    PermissionCode.FieldControlViewOwn,
    PermissionCode.FieldControlUpdateOwn,
    PermissionCode.FieldControlViewAll,
    PermissionCode.FieldControlManageAssignments,
    PermissionCode.ChecklistRead,
    PermissionCode.ChecklistUpdate,
    PermissionCode.RecommendationRead,
    PermissionCode.RecommendationCreate,
    PermissionCode.RecommendationUpdate,
    PermissionCode.ReportRead,
    PermissionCode.ReportReadAssigned,
    PermissionCode.RiRead,
    PermissionCode.NdtRead,
    PermissionCode.NdtCreate,
    PermissionCode.NdtUpdate,
    PermissionCode.NdtRegistryRead,
    PermissionCode.NdtRegistryCreate,
    PermissionCode.NdtRegistryUpdate,
    PermissionCode.RdoRead,
    PermissionCode.RdoCreate,
    PermissionCode.RdoUpdate,
    PermissionCode.DocumentRead,
    PermissionCode.DocumentWrite,
    PermissionCode.CatalogRead,
    PermissionCode.ProfessionalRead,
    PermissionCode.SignatoryRead,
    // Bloco 02: EngineeringRequestCorrection REMOVIDA — é ato exclusivo da
    // Engenharia (só existe dentro do Workspace de Engenharia, área negada a
    // este perfil). Solicitar correção continua sendo do PLH/Engenharia.
    PermissionCode.SubOsSearch,
    PermissionCode.SubWorkOrderRead,
    PermissionCode.SubWorkOrderCreate,
    PermissionCode.SubWorkOrderUpdate,
    PermissionCode.ProfessionalSubOsRead,
    // ME (Bloco 11): thickness measurement is filed under END/NDT, where
    // Technician already has full Ndt*/NdtRegistry* reach above.
    PermissionCode.MeReportView,
    PermissionCode.MeReportCreate,
    PermissionCode.MeReportEdit,
    PermissionCode.MeReportGeneratePdf,
    // LP: mesmo alcance do ME para o papel — Approve/Sign continuam só do PLH.
    PermissionCode.LpReportView,
    PermissionCode.LpReportCreate,
    PermissionCode.LpReportEdit,
    PermissionCode.LpReportValidate,
    PermissionCode.LpReportGeneratePdf,
    // PM e US-Convencional: mesmo alcance do LP para o papel — Approve/Sign continuam só do PLH.
    PermissionCode.PmReportView,
    PermissionCode.PmReportCreate,
    PermissionCode.PmReportEdit,
    PermissionCode.PmReportValidate,
    PermissionCode.PmReportGeneratePdf,
    PermissionCode.UscReportView,
    PermissionCode.BscanReportView,
    PermissionCode.UscReportCreate,
    PermissionCode.BscanReportCreate,
    PermissionCode.UscReportEdit,
    PermissionCode.BscanReportEdit,
    PermissionCode.UscReportValidate,
    PermissionCode.BscanReportValidate,
    PermissionCode.UscReportGeneratePdf,
    PermissionCode.BscanReportGeneratePdf,
    // Enviar o relatório ao PLH é ato de quem elabora; a fila é da Engenharia.
    PermissionCode.TechnicalReviewSubmitToPlh
  ],
  [RoleCode.Engineer]: [
    // M16A Etapa 5: ENGINEER tem acesso ao Painel de Avanços e Indicadores.
    PermissionCode.DashboardIndicatorsView,
    PermissionCode.ClientAccess,
    PermissionCode.ClientRead,
    PermissionCode.ProjectRead,
    PermissionCode.ProjectCreate,
    PermissionCode.ProjectUpdate,
    PermissionCode.ProjectManage,
    PermissionCode.UnitAreaRead,
    PermissionCode.UnitAreaCreate,
    PermissionCode.UnitAreaUpdate,
    PermissionCode.EquipmentRead,
    PermissionCode.EquipmentCreate,
    PermissionCode.EquipmentUpdate,
    PermissionCode.EquipmentInactivate,
    PermissionCode.EquipmentReactivate,
    PermissionCode.EquipmentImport, // Bloco 04: importação em lote de Sistemas/Tubulações.
    PermissionCode.TechnicalAssetDocumentsManage,
    PermissionCode.InstrumentRead,
    PermissionCode.InstrumentCreate,
    PermissionCode.InstrumentUpdate,
    PermissionCode.InstrumentInactivate,
    PermissionCode.InstrumentReactivate,
    PermissionCode.SafetyDeviceRead,
    PermissionCode.SafetyDeviceCreate,
    PermissionCode.SafetyDeviceUpdate,
    PermissionCode.SafetyDeviceInactivate,
    PermissionCode.SafetyDeviceReactivate,
    PermissionCode.InspectionRead,
    PermissionCode.InspectionReadAll,
    PermissionCode.InspectionCreate,
    PermissionCode.InspectionUpdate,
    // CONTROLE DE CAMPO: o Engenheiro acompanha o avanço de todos os
    // Inspetores e também pode vincular Inspetor → Sistema.
    PermissionCode.FieldControlViewOwn,
    PermissionCode.FieldControlUpdateOwn,
    PermissionCode.FieldControlViewAll,
    PermissionCode.FieldControlManageAssignments,
    PermissionCode.ChecklistRead,
    PermissionCode.ChecklistUpdate,
    PermissionCode.RecommendationRead,
    PermissionCode.RecommendationCreate,
    PermissionCode.RecommendationUpdate,
    PermissionCode.ReportRead,
    PermissionCode.ReportReadAll,
    PermissionCode.ReportCreate,
    PermissionCode.ReportUpdate,
    PermissionCode.ReportReview,
    PermissionCode.ReportApprove,
    PermissionCode.ReportSign,
    PermissionCode.RiRead,
    PermissionCode.RiCreate,
    PermissionCode.RiUpdate,
    PermissionCode.NdtRead,
    PermissionCode.NdtCreate,
    PermissionCode.NdtUpdate,
    PermissionCode.NdtRegistryRead,
    PermissionCode.NdtRegistryCreate,
    PermissionCode.NdtRegistryUpdate,
    PermissionCode.NdtRegistryInactivate,
    // Procedimentos de END (Bloco 14): only ENGINEER may physically delete a
    // registry record, and only when nothing references it.
    PermissionCode.NdtRegistryDelete,
    PermissionCode.EngineeringAccess,
    PermissionCode.CalculationAccess,
    PermissionCode.AvrAccess,
    PermissionCode.AvrRead,
    PermissionCode.AvrCreate,
    PermissionCode.AvrUpdate,
    // AVR: o Engenheiro escolhe as fontes, calcula, valida e emite o PDF.
    // AvrApprove/AvrSign são de PLH — ver PLH_SPECIFIC_PERMISSIONS.
    PermissionCode.AvrSelectSources,
    PermissionCode.AvrCalculate,
    PermissionCode.AvrValidate,
    PermissionCode.AvrGeneratePdf,
    // Enviar ao PLH é ato do Engenheiro elaborador — quem analisa é outro (o PLH).
    PermissionCode.AvrSubmitForPlhReview,
    PermissionCode.TechnicalReviewViewQueue,
    // Painel de Engenharia: dashboard, revisão (leitura), Minuta, HOLD (ver/comentar), HOLD nos próprios documentos.
    PermissionCode.EngineeringDashboardView,
    PermissionCode.EngineeringReviewView,
    PermissionCode.EngineeringMinuteCreate,
    PermissionCode.EngineeringMinuteEdit,
    // HOTFIX P0-RBAC-MINUTA-01: Engenheiro elaborador precisa solicitar revisão
    // da Minuta (REV.00 → REV.01). Sem esta permissão o RPC createUpdateRevision
    // retorna Forbidden em produção. DOCUMENTATION já a possui; MANAGER/INSPECTOR não.
    PermissionCode.MinutaRequestRevision,
    PermissionCode.EngineeringHoldView,
    PermissionCode.EngineeringHoldComment,
    PermissionCode.ReportPutOnHold,
    PermissionCode.ReportRemoveFromHold,
    PermissionCode.PlhReview,
    PermissionCode.PlhApprove,
    PermissionCode.PlhReturn,
    PermissionCode.SignatureSign,
    PermissionCode.RdoRead,
    PermissionCode.RdoCreate,
    PermissionCode.RdoUpdate,
    PermissionCode.RdoConsolidate,
    PermissionCode.DocumentRead,
    PermissionCode.DocumentWrite,
    PermissionCode.DocumentSign,
    PermissionCode.DocumentIssue,
    PermissionCode.DocumentRevise,
    // Bloco 03: inativação lógica de documento técnico.
    PermissionCode.DocumentCancel,
    PermissionCode.ManagementAccess,
    PermissionCode.AdministrationRead,
    PermissionCode.ProfessionalRead,
    PermissionCode.ProfessionalManage,
    PermissionCode.ProfessionalSubOsRead,
    PermissionCode.ProfessionalSubOsAssign,
    PermissionCode.ProfessionalSubOsRemove,
    PermissionCode.ProfessionalSubOsInactivate,
    PermissionCode.SubWorkOrderRead,
    PermissionCode.SubWorkOrderCreate,
    PermissionCode.SubWorkOrderUpdate,
    PermissionCode.SubWorkOrderDelete,
    PermissionCode.SignatoryRead,
    PermissionCode.SignatoryManage,
    PermissionCode.InspectorSearch,
    PermissionCode.SubOsSearch,
    PermissionCode.CatalogRead,
    PermissionCode.CatalogCreate,
    PermissionCode.CatalogUpdate,
    PermissionCode.CatalogInactivate,
    PermissionCode.AuditRead,
    // INSPECT IA REVIEW (substitui o antigo cortex.use): o Engenheiro elabora
    // relatórios e consulta a revisão IA dos que analisa.
    PermissionCode.AiReviewRead,
    PermissionCode.AiReviewExecute,
    PermissionCode.AiReviewApplyLanguage,
    PermissionCode.AiReviewResolveFinding,
    PermissionCode.AiReviewHistory,
    PermissionCode.AiAssistantUse,
    PermissionCode.EngineeringViewWorkspace,
    PermissionCode.EngineeringStartAnalysis,
    PermissionCode.EngineeringEditRecommendation,
    PermissionCode.EngineeringRequestCorrection,
    PermissionCode.EngineeringDelegateToInspector,
    PermissionCode.EngineeringValidateCorrection,
    // "Validar Minuta" = dar a Minuta como CONFORME (Bloco 6, 2026-09-14).
    // Replaces the deprecated EngineeringCompleteAnalysis — that permission
    // gated a redundant "Concluir Análise" step between validation and PLH
    // approval; the workflow now goes straight from EM ANÁLISE to CONFORME.
    PermissionCode.EngineeringValidateMinuta,
    PermissionCode.MinutaReturnForCorrection,
    // RI (Integrity Report) review actions, mirroring the Minuta ones above
    // (Bloco 9). IntegrityReportApprove/Sign are PLH-specific, not granted
    // here — see PLH_SPECIFIC_PERMISSIONS.
    PermissionCode.IntegrityReportView,
    PermissionCode.IntegrityReportEdit,
    PermissionCode.IntegrityReportValidate,
    PermissionCode.IntegrityReportGeneratePdf,
    // Designar o Inspetor Responsável do RI (mesma regra do ME abaixo): o
    // próprio Inspetor e o Técnico não a recebem — para eles o campo é
    // resolvido pelo Professional da própria conta.
    PermissionCode.ReportAssignInspector,
    // ME (Bloco 11, 2026-09-15): Relatório de Medição de Espessura review
    // actions, mirroring the RI permissions above. MeReportApprove/Sign are
    // PLH-specific, not granted here — see PLH_SPECIFIC_PERMISSIONS.
    PermissionCode.MeReportView,
    PermissionCode.MeReportCreate,
    PermissionCode.MeReportEdit,
    PermissionCode.MeReportValidate,
    PermissionCode.MeReportGeneratePdf,
    // Bloco 01 (2026-09-25): a Engenharia designa o Inspetor de um ME. O
    // Inspetor e o Técnico NÃO a recebem — para eles o campo é resolvido pelo
    // Professional da própria conta, e emitir em nome de outra pessoa exige
    // esta permissão.
    PermissionCode.MeReportAssignInspector,
    // LP: mesmo alcance do ME para o papel — Approve/Sign continuam só do PLH.
    PermissionCode.LpReportView,
    PermissionCode.LpReportCreate,
    PermissionCode.LpReportEdit,
    PermissionCode.LpReportValidate,
    PermissionCode.LpReportGeneratePdf,
    // PM e US-Convencional: mesmo alcance do LP para o papel — Approve/Sign continuam só do PLH.
    PermissionCode.PmReportView,
    PermissionCode.PmReportCreate,
    PermissionCode.PmReportEdit,
    PermissionCode.PmReportValidate,
    PermissionCode.PmReportGeneratePdf,
    PermissionCode.UscReportView,
    PermissionCode.BscanReportView,
    PermissionCode.UscReportCreate,
    PermissionCode.BscanReportCreate,
    PermissionCode.UscReportEdit,
    PermissionCode.BscanReportEdit,
    PermissionCode.UscReportValidate,
    PermissionCode.BscanReportValidate,
    PermissionCode.UscReportGeneratePdf,
    PermissionCode.BscanReportGeneratePdf,
    // Enviar o relatório ao PLH é ato de quem elabora; a fila é da Engenharia.
    PermissionCode.TechnicalReviewSubmitToPlh,
    // Numeração ZR da Recommendation (Bloco 22): corrigir ou retirar o número
    // é decisão de engenharia sobre o documento. Só o ENGINEER recebe pelo
    // papel — Técnico, Inspetor, Documentação e Admin não.
    PermissionCode.EngineeringEditZrNumber,
    PermissionCode.EngineeringRemoveZrNumber,
    // Signatários do LP: só o ENGINEER escolhe Inspetor, PLH e Fiscal. Os
    // demais perfis veem os cards em modo leitura. Escolher o PLH não torna
    // ninguém PLH — quem assina continua passando pelo gate canActAsPlh().
    PermissionCode.LpReportManageSignatories,
    PermissionCode.PmReportManageSignatories,
    PermissionCode.UscReportManageSignatories,
    PermissionCode.BscanReportManageSignatories
    // EngineeringApproveMinuta / EngineeringSignMinuta are deliberately NOT
    // granted to the base ENGINEER role: approving/signing as PLH is a
    // specific technical capability tied to a qualified Professional
    // (Professional.plhClientId + signatureImageId — see
    // src/domain/identity/plh-eligibility.ts), not something every Engineer
    // profile gets automatically. An administrator can still grant either
    // permission to a specific user via "Editar Permissões"
    // (customPermissions are additive on top of the role — see
    // src/permissions/effective-permissions.ts). Real enforcement of both
    // permissions plus the PLH professional-qualification gate was added to
    // engineering-service.ts in Bloco 6.
  ],
  [RoleCode.Documentation]: [
    PermissionCode.ClientAccess,
    PermissionCode.ClientRead,
    PermissionCode.DocumentRead,
    PermissionCode.DocumentWrite,
    // Bloco 03: inativação lógica de documento técnico.
    PermissionCode.DocumentCancel,
    PermissionCode.InspectionRead,
    PermissionCode.InspectionReadAll,
    PermissionCode.ReportRead,
    PermissionCode.ReportReadAll,
    PermissionCode.RiRead,
    PermissionCode.MeReportView,
    PermissionCode.LpReportView,
    PermissionCode.PmReportView,
    PermissionCode.UscReportView,
    PermissionCode.BscanReportView,
    PermissionCode.AvrRead,
    PermissionCode.CatalogRead,
    PermissionCode.NdtRegistryRead,
    PermissionCode.NdtRead,
    PermissionCode.MinutaRequestRevision,
    PermissionCode.ProfessionalRead,
    PermissionCode.RdoRead,
    PermissionCode.RdoCreate,
    PermissionCode.RdoUpdate,
    PermissionCode.SubWorkOrderRead,
    PermissionCode.SubOsSearch,
    // EXCEÇÃO OFICIAL: Técnico de Documentação pode gerenciar recomendações
    PermissionCode.RecommendationRead,
    PermissionCode.RecommendationCreate,
    PermissionCode.RecommendationUpdate,
    // INSPECT IA REVIEW — consulta
    PermissionCode.AiReviewRead,
    PermissionCode.AiReviewHistory
  ],
  [RoleCode.Manager]: [
    // M16A Etapa 5: MANAGER tem acesso ao Painel de Avanços e Indicadores.
    PermissionCode.DashboardIndicatorsView,
    PermissionCode.ClientAccess,
    PermissionCode.ClientRead,
    PermissionCode.IdentityRead,
    PermissionCode.AdministrationRead,
    PermissionCode.AuditRead,
    PermissionCode.EquipmentRead,
    PermissionCode.EquipmentCreate,
    PermissionCode.EquipmentUpdate,
    PermissionCode.EquipmentImport, // Bloco 04: importação em lote de Sistemas/Tubulações.
    PermissionCode.InspectionRead,
    PermissionCode.InspectionCreate,
    PermissionCode.InspectionUpdate,
    PermissionCode.ReportRead,
    PermissionCode.ReportCreate,
    PermissionCode.ReportUpdate,
    PermissionCode.DocumentRead,
    PermissionCode.DocumentWrite,
    PermissionCode.CatalogRead,
    PermissionCode.CatalogCreate,
    PermissionCode.CatalogUpdate,
    PermissionCode.CatalogInactivate,
    // Correção 9: o Gestor apresenta o avanço ao Cliente — cria, vê e revoga os
    // PRÓPRIOS links do Painel. Ver e revogar os de todos é do Administrador.
    PermissionCode.DashboardShareCreate,
    PermissionCode.DashboardShareRead,
    PermissionCode.DashboardShareRevoke,
    // INSPECT IA REVIEW — consulta.
    PermissionCode.AiReviewRead,
    PermissionCode.AiReviewHistory
  ],
  [RoleCode.Developer]: [
    // M16A Etapa 5: DEVELOPER tem acesso ao Painel de Avanços e Indicadores.
    PermissionCode.DashboardIndicatorsView,
    PermissionCode.ClientAccess,
    PermissionCode.AiReviewRead,
    PermissionCode.AiReviewHistory,
    PermissionCode.AiSettingsRead,
    PermissionCode.IdentityRead,
    PermissionCode.AdministrationRead,
    PermissionCode.CatalogRead,
    PermissionCode.CatalogCreate,
    PermissionCode.CatalogUpdate,
    PermissionCode.CatalogInactivate,
    PermissionCode.StudioCreate,
    PermissionCode.StudioUpdate,
    PermissionCode.StudioTest,
    PermissionCode.StudioPublish,
    PermissionCode.StudioInactivate,
    PermissionCode.StudioArchive,
    PermissionCode.SettingsRead,
    PermissionCode.SettingsIntegrationRead,
    PermissionCode.BackupRead,
    PermissionCode.BackupCreate,
    PermissionCode.StudioRead,
    PermissionCode.StudioIdentityRead,
    PermissionCode.StudioThemeRead,
    PermissionCode.StudioDocumentsRead,
    PermissionCode.StudioTemplatesRead,
    PermissionCode.SettingsDiagnosticsRead,
    PermissionCode.SettingsDiagnosticsExecute,
    PermissionCode.EquipmentRead,
    PermissionCode.InspectionRead,
    PermissionCode.ChecklistRead,
    PermissionCode.ChecklistUpdate,
    PermissionCode.RecommendationRead,
    PermissionCode.RecommendationCreate,
    PermissionCode.RecommendationUpdate,
    PermissionCode.RiRead,
    PermissionCode.RiCreate,
    PermissionCode.RiUpdate,
    PermissionCode.NdtRead,
    PermissionCode.NdtCreate,
    PermissionCode.NdtUpdate,
    PermissionCode.EngineeringAccess,
    PermissionCode.CalculationAccess,
    PermissionCode.AvrAccess,
    PermissionCode.AvrRead,
    PermissionCode.AvrCreate,
    PermissionCode.AvrUpdate,
    PermissionCode.AvrSelectSources,
    PermissionCode.AvrCalculate,
    PermissionCode.AvrValidate,
    PermissionCode.AvrGeneratePdf,
    PermissionCode.AvrSubmitForPlhReview,
    PermissionCode.TechnicalReviewViewQueue,
    PermissionCode.EngineeringDashboardView,
    PermissionCode.EngineeringReviewView,
    PermissionCode.EngineeringMinuteCreate,
    PermissionCode.EngineeringMinuteEdit,
    PermissionCode.EngineeringHoldView,
    PermissionCode.EngineeringHoldComment,
    PermissionCode.ReportPutOnHold,
    PermissionCode.ReportRemoveFromHold,
    PermissionCode.EngineeringViewWorkspace,
    PermissionCode.EngineeringStartAnalysis,
    PermissionCode.EngineeringEditRecommendation,
    PermissionCode.EngineeringRequestCorrection,
    PermissionCode.EngineeringDelegateToInspector,
    PermissionCode.EngineeringValidateCorrection,
    PermissionCode.EngineeringValidateMinuta,
    PermissionCode.MinutaReturnForCorrection,
    PermissionCode.IntegrityReportView,
    PermissionCode.IntegrityReportEdit,
    PermissionCode.IntegrityReportValidate,
    PermissionCode.IntegrityReportGeneratePdf,
    PermissionCode.ReportAssignInspector,
    PermissionCode.MeReportView,
    PermissionCode.MeReportCreate,
    PermissionCode.MeReportEdit,
    PermissionCode.MeReportValidate,
    PermissionCode.MeReportGeneratePdf,
    PermissionCode.MeReportAssignInspector,
    PermissionCode.LpReportView,
    PermissionCode.LpReportCreate,
    PermissionCode.LpReportEdit,
    PermissionCode.LpReportValidate,
    PermissionCode.LpReportGeneratePdf,
    PermissionCode.PmReportView,
    PermissionCode.PmReportCreate,
    PermissionCode.PmReportEdit,
    PermissionCode.PmReportValidate,
    PermissionCode.PmReportGeneratePdf,
    PermissionCode.UscReportView,
    PermissionCode.BscanReportView,
    PermissionCode.UscReportCreate,
    PermissionCode.BscanReportCreate,
    PermissionCode.UscReportEdit,
    PermissionCode.BscanReportEdit,
    PermissionCode.UscReportValidate,
    PermissionCode.BscanReportValidate,
    PermissionCode.UscReportGeneratePdf,
    PermissionCode.BscanReportGeneratePdf,
    PermissionCode.TechnicalReviewSubmitToPlh,
    PermissionCode.NdtRegistryRead,
    PermissionCode.NdtRegistryCreate,
    PermissionCode.NdtRegistryUpdate,
    PermissionCode.NdtRegistryInactivate,
    PermissionCode.NdtRegistryDelete,
    PermissionCode.BackupRead,
    PermissionCode.BackupCreate,
    PermissionCode.BackupRestore,
    PermissionCode.BackupDelete,
    PermissionCode.BackupManage,
    PermissionCode.RdoRead,
    PermissionCode.RdoCreate,
    PermissionCode.RdoUpdate,
    PermissionCode.SubOsSearch,
    PermissionCode.SubWorkOrderRead,
    PermissionCode.SubWorkOrderCreate,
    PermissionCode.SubWorkOrderUpdate,
    PermissionCode.SubWorkOrderDelete,
    PermissionCode.ProfessionalSubOsRead,
    PermissionCode.ProfessionalSubOsAssign,
    PermissionCode.ProfessionalSubOsRemove,
    PermissionCode.ProfessionalSubOsInactivate
  ],
  [RoleCode.Admin]: Object.values(PermissionCode).filter(
    (permission) =>
      !(PLH_SPECIFIC_PERMISSIONS as readonly PermissionCodeValue[]).includes(permission) &&
      !(ENGINEERING_SPECIFIC_PERMISSIONS as readonly PermissionCodeValue[]).includes(permission)
  )
};

/**
 * A matriz oficial = permissões base ∪ permissões do perfil. É a ÚNICA fonte
 * que `resolveEffectivePermissions` lê — no navegador, no /api/session e no
 * contexto de cada /api/rpc —, recalculada a cada sessão: vale para usuários
 * antigos sem backfill e para os novos sem configurar nada.
 */
export const BUILT_IN_ROLE_PERMISSIONS: Readonly<
  Record<BuiltInRoleCode, readonly PermissionCodeValue[]>
> = Object.fromEntries(
  (Object.entries(ROLE_SPECIFIC_PERMISSIONS) as Array<[BuiltInRoleCode, readonly PermissionCodeValue[]]>)
    .map(([role, permissions]) => [role, [...new Set<PermissionCodeValue>([...BASE_AUTHENTICATED_PERMISSIONS, ...permissions])]])
) as unknown as Record<BuiltInRoleCode, readonly PermissionCodeValue[]>;
